/**
 * belegKonto — Lernender Buchungskonto-/Kategorie-Vorschlag für Eingangsbelege
 * (Feature beleg-pipeline-lernend).
 *
 * Zwei Stufen:
 *   1) buchungs_lernregeln (firma + lieferant_muster) — je öfter eine Zuordnung
 *      bestätigt wurde (treffer), desto sicherer der Vorschlag.
 *   2) Fallback: Keyword-Heuristik (schlageKategorieNameVor aus useBankAbgleich)
 *      → passende finanz_kategorie per Name.
 *
 * GoBD: erzeugt ausschließlich VORSCHLÄGE. Die UI belegt damit nur vor; jede
 * Buchung/Zuordnung wird einzeln bestätigt. RLS (Inhaber-only) unverändert.
 * Robuste Queries: try/catch, kein Absturz bei leerer/fehlender Tabelle.
 */

import { supabase } from '@/lib/supabase';
import { schlageKategorieNameVor } from '@/hooks/useBankAbgleich';

export interface KontoVorschlag {
  kategorie_id: string | null;
  konto_skr: string | null;
  /** Name der vorgeschlagenen Kategorie (für die UI-Anzeige), falls ermittelbar. */
  kategorie_name: string | null;
  /** Woher der Vorschlag stammt — zur transparenten Anzeige. */
  quelle: 'lernregel' | 'keyword' | 'keiner';
}

function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

/**
 * Normalisiert einen Lieferanten-/Belegtext zu einem schlanken Muster-Token:
 * lowercase, ohne Sonderzeichen, kollabierte Whitespaces. Wird sowohl als
 * Suchbegriff als auch als gespeichertes lieferant_muster verwendet.
 */
export function normalisiereLieferant(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9äöüß\s]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface FinanzKategorieMini {
  id: string;
  name: string;
  typ: string;
}

/** Lädt alle finanz_kategorien (robust, leeres Array bei Fehler). */
async function ladeKategorien(): Promise<FinanzKategorieMini[]> {
  try {
    const { data, error } = await supabase
      .from('finanz_kategorien')
      .select('id, name, typ');
    if (error) throw error;
    return (data as FinanzKategorieMini[]) || [];
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Kategorien laden fehlgeschlagen:', err);
    return [];
  }
}

/**
 * Liefert einen Buchungskonto-/Kategorie-Vorschlag für einen Beleg.
 *
 * @param firma   Wert wie in buchungs_lernregeln.firma gespeichert
 * @param text    Lieferant/Verwendungszweck/Beschreibung (frei)
 */
export async function schlageKontoVor(
  firma: string,
  text: string | null | undefined,
): Promise<KontoVorschlag> {
  const muster = normalisiereLieferant(text);

  // ── 1) Lernregeln durchsuchen ──────────────────────────────
  if (muster) {
    try {
      const { data, error } = await supabase
        .from('buchungs_lernregeln')
        .select('lieferant_muster, kategorie_id, konto_skr, treffer')
        .eq('firma', firma)
        .order('treffer', { ascending: false })
        .limit(200);
      if (error) throw error;

      const regeln = (data as {
        lieferant_muster: string;
        kategorie_id: string | null;
        konto_skr: string | null;
        treffer: number | null;
      }[]) || [];

      // Beste Regel: Muster enthalten/gleich, höchste treffer-Zahl zuerst.
      let beste: typeof regeln[number] | null = null;
      for (const r of regeln) {
        const rm = normalisiereLieferant(r.lieferant_muster);
        if (!rm) continue;
        const passt = muster === rm || muster.includes(rm) || rm.includes(muster);
        if (!passt) continue;
        if (!beste || (r.treffer || 0) > (beste.treffer || 0)) beste = r;
      }

      if (beste) {
        let kategorie_name: string | null = null;
        if (beste.kategorie_id) {
          const kats = await ladeKategorien();
          kategorie_name = kats.find(k => k.id === beste!.kategorie_id)?.name || null;
        }
        return {
          kategorie_id: beste.kategorie_id ?? null,
          konto_skr: beste.konto_skr ?? null,
          kategorie_name,
          quelle: 'lernregel',
        };
      }
    } catch (err) {
      if (!isTableNotFound(err)) console.error('Lernregel-Suche fehlgeschlagen:', err);
    }
  }

  // ── 2) Fallback: Keyword-Heuristik → finanz_kategorie per Name ──
  try {
    const name = schlageKategorieNameVor(text || '', false);
    const kats = await ladeKategorien();
    const nameLc = name.toLowerCase();
    const treffer =
      kats.find(k => k.name.toLowerCase() === nameLc) ||
      kats.find(k => k.name.toLowerCase().includes(nameLc)) ||
      null;
    if (treffer) {
      return {
        kategorie_id: treffer.id,
        konto_skr: null,
        kategorie_name: treffer.name,
        quelle: 'keyword',
      };
    }
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Keyword-Fallback fehlgeschlagen:', err);
  }

  return { kategorie_id: null, konto_skr: null, kategorie_name: null, quelle: 'keiner' };
}

/**
 * Bestätigt/verstärkt eine Lernregel (Upsert über firma + lieferant_muster):
 * vorhandene treffer + 1, zuletzt_genutzt = now. So wird das System mit jeder
 * Bestätigung sicherer. Wirft NICHT — Lernen darf den Speichern-Flow nie blockieren.
 */
export async function lerneLernregel(params: {
  firma: string;
  text: string | null | undefined;
  kategorie_id: string | null;
  konto_skr?: string | null;
  typ?: string | null;
}): Promise<void> {
  const muster = normalisiereLieferant(params.text);
  if (!muster) return;
  if (!params.kategorie_id && !params.konto_skr) return;

  try {
    // Bisherigen Trefferzähler lesen (für inkrementelles Lernen).
    let treffer = 1;
    try {
      const { data } = await supabase
        .from('buchungs_lernregeln')
        .select('treffer')
        .eq('firma', params.firma)
        .eq('lieferant_muster', muster)
        .maybeSingle();
      const vorhanden = (data as { treffer: number | null } | null)?.treffer;
      if (typeof vorhanden === 'number' && Number.isFinite(vorhanden)) {
        treffer = vorhanden + 1;
      }
    } catch {
      // ignorieren — Default treffer = 1
    }

    const row: Record<string, unknown> = {
      firma: params.firma,
      lieferant_muster: muster,
      kategorie_id: params.kategorie_id ?? null,
      konto_skr: params.konto_skr ?? null,
      typ: params.typ ?? null,
      treffer,
      zuletzt_genutzt: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('buchungs_lernregeln')
      .upsert(row as any, { onConflict: 'firma,lieferant_muster' });
    if (error) throw error;
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Lernregel-Upsert fehlgeschlagen:', err);
  }
}
